import React, { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Eye,
  Loader2,
  Phone,
  Receipt,
  Search,
  User,
  Wallet,
  X,
} from "lucide-react";

const formatMoney = (value) =>
  `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(value) || 0)} F`;

const isCreditMethod = (method = {}) => {
  const token = `${method.id || method.code || method.name || ""}`
    .toLowerCase()
    .trim();
  return token.includes("credit") || token.includes("crédit");
};

const CreditRecoveryModal = ({
  isOpen,
  onClose,
  currentUser,
  currentSession,
  apiBase = "/api",
  onRecoveryCompleted,
}) => {
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCreditor, setSelectedCreditor] = useState(null);
  const [receivables, setReceivables] = useState([]);
  const [loadingReceivables, setLoadingReceivables] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showPaymentPanel, setShowPaymentPanel] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [ticketError, setTicketError] = useState("");
  const [ticketDetails, setTicketDetails] = useState(null);
  const [ticketTarget, setTicketTarget] = useState(null);

  const closePanel = () => {
    setShowPaymentPanel(false);
    setPaymentTarget(null);
    setPaymentAmount("");
    setPaymentMethod("");
    setPaymentNote("");
    setPinCode("");
  };

  const closeTicketPanel = () => {
    setShowTicketModal(false);
    setLoadingTicket(false);
    setTicketError("");
    setTicketDetails(null);
    setTicketTarget(null);
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await fetch(`${apiBase}/payment-methods/all`);
      if (!response.ok) return;
      const data = await response.json();
      const methods = Array.isArray(data) ? data : [];
      const filtered = methods.filter((method) => !isCreditMethod(method));
      setPaymentMethods(filtered);
    } catch (_e) {
      // non bloquant
    }
  };

  const loadCreditors = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${apiBase}/credit-recovery/creditors`, {
        cache: "no-store",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Erreur chargement recouvrements");
      }
      const data = await response.json();
      setCreditors(Array.isArray(data.creditors) ? data.creditors : []);
    } catch (err) {
      setError(err.message || "Erreur serveur");
    } finally {
      setLoading(false);
    }
  };

  const loadCreditorReceivables = async (creditor) => {
    if (!creditor?.creditor_ref) return;
    try {
      setSelectedCreditor(creditor);
      setLoadingReceivables(true);
      setError("");
      const response = await fetch(
        `${apiBase}/credit-recovery/creditors/${encodeURIComponent(creditor.creditor_ref)}/receivables`,
        { cache: "no-store" },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Erreur chargement des créances");
      }
      const data = await response.json();
      setReceivables(Array.isArray(data.receivables) ? data.receivables : []);
    } catch (err) {
      setError(err.message || "Erreur serveur");
    } finally {
      setLoadingReceivables(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setSuccess("");
    setError("");
    setSearchTerm("");
    setSelectedCreditor(null);
    setReceivables([]);
    closePanel();
    void Promise.all([loadCreditors(), loadPaymentMethods()]);
  }, [isOpen]);

  const filteredCreditors = useMemo(() => {
    if (!searchTerm.trim()) return creditors;
    const term = searchTerm.trim().toLowerCase();
    return creditors.filter((creditor) =>
      `${creditor.customer_name || ""} ${creditor.customer_phone || ""}`
        .toLowerCase()
        .includes(term),
    );
  }, [creditors, searchTerm]);

  const formatDateTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString("fr-FR");
  };

  const formatLineItemName = (item) => {
    if (!item) return "Article";
    const baseName = item.product_name || item.name || "Article";
    const variation =
      item.variation_label || item.variation_name || item.variationLabel || "";
    if (variation && variation.toString().trim()) {
      return `${baseName} · ${variation.toString().trim()}`;
    }
    return baseName;
  };

  const openPayPanel = (target) => {
    const due = Number(target?.amount_due ?? target?.total_due ?? 0);
    setPaymentTarget(target);
    setPaymentAmount(due > 0 ? `${due}` : "");
    setPaymentMethod("");
    setShowPaymentPanel(true);
    setSuccess("");
    setError("");
  };

  const openTicketDetails = async (row) => {
    if (!row?.order_id) {
      setError("Ticket introuvable");
      return;
    }

    setTicketTarget(row);
    setShowTicketModal(true);
    setLoadingTicket(true);
    setTicketError("");
    setTicketDetails(null);

    try {
      const response = await fetch(`${apiBase}/sales/${row.order_id}/details`, {
        cache: "no-store",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || "Impossible de charger le ticket");
      }
      const data = await response.json();
      setTicketDetails(data || null);
    } catch (err) {
      setTicketError(err.message || "Erreur serveur");
    } finally {
      setLoadingTicket(false);
    }
  };

  const submitPayment = async () => {
    if (!paymentTarget) return;
    const normalizedAmount = Number(paymentAmount);
    if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) {
      setError("Montant invalide");
      return;
    }
    if (!paymentMethod) {
      setError("Moyen de paiement requis");
      return;
    }
    if (!pinCode.trim()) {
      setError("Code de validation requis");
      return;
    }
    if (!currentUser?.id) {
      setError("Utilisateur non identifié");
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");
      const payload = {
        amount: normalizedAmount,
        paymentMethod,
        notes: paymentNote || null,
        pinCode,
        userId: currentUser.id,
        sessionId: currentSession?.id || null,
      };
      if (paymentTarget.type === "single") {
        payload.receivableId = paymentTarget.receivable_id;
      } else {
        payload.creditorRef = paymentTarget.creditor_ref;
      }

      const response = await fetch(`${apiBase}/credit-recovery/pay`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || "Échec du recouvrement");
      }

      setSuccess("Recouvrement enregistré avec succès");
      closePanel();
      await loadCreditors();
      if (selectedCreditor) {
        await loadCreditorReceivables(selectedCreditor);
      }
      if (typeof onRecoveryCompleted === "function") {
        onRecoveryCompleted();
      }
    } catch (err) {
      setError(err.message || "Erreur serveur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-6xl max-h-[90vh] overflow-hidden rounded-3xl border border-white/10 bg-[#0f172a] text-white shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-amber-500/20 via-indigo-500/10 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">
              <Wallet size={20} className="text-amber-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-amber-200">
                Crédit client
              </p>
              <h3 className="text-2xl font-semibold">Recouvrement</h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="h-11 w-11 rounded-2xl border border-white/10 bg-white/10 hover:bg-white/20 transition flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-0 h-[calc(90vh-88px)]">
          <div className="border-r border-white/10 flex flex-col">
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un créancier..."
                  className="w-full rounded-2xl border border-white/15 bg-white/5 pl-10 pr-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-amber-300"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loading && (
                <div className="text-sm text-white/60 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement des créanciers...
                </div>
              )}
              {!loading && filteredCreditors.length === 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-5 text-sm text-white/60">
                  Aucun créancier à recouvrer.
                </div>
              )}
              {!loading &&
                filteredCreditors.map((creditor) => (
                  <div
                    key={creditor.creditor_ref}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-base font-semibold flex items-center gap-2">
                          <User size={14} className="text-amber-300" />
                          {creditor.customer_name || "Créancier"}
                        </p>
                        {creditor.customer_phone && (
                          <p className="text-xs text-white/60 flex items-center gap-2">
                            <Phone size={12} />
                            {creditor.customer_phone}
                          </p>
                        )}
                        <p className="text-xs text-white/50">
                          {creditor.receivables_count || 0} ticket(s) à crédit
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/50">Montant dû</p>
                        <p className="text-lg font-semibold text-rose-300">
                          {formatMoney(creditor.total_due)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={() => loadCreditorReceivables(creditor)}
                        className="h-9 w-9 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 flex items-center justify-center"
                        title="Voir"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() =>
                          openPayPanel({
                            type: "global",
                            creditor_ref: creditor.creditor_ref,
                            amount_due: creditor.total_due,
                            display_name: creditor.customer_name,
                          })
                        }
                        className="h-9 w-9 rounded-xl border border-emerald-300/40 bg-emerald-400/15 hover:bg-emerald-400/25 text-emerald-200 flex items-center justify-center"
                        title="Payer"
                      >
                        <CreditCard size={16} />
                      </button>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className="flex flex-col">
            <div className="px-4 py-3 border-b border-white/10">
              <h4 className="text-lg font-semibold">
                {selectedCreditor
                  ? `Détails · ${selectedCreditor.customer_name}`
                  : "Sélectionnez un créancier"}
              </h4>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!selectedCreditor && (
                <div className="rounded-2xl border border-dashed border-white/20 bg-white/5 px-4 py-5 text-sm text-white/60">
                  Utilisez le bouton <span className="font-semibold">Voir</span> pour afficher les
                  créances d’un client.
                </div>
              )}

              {loadingReceivables && (
                <div className="text-sm text-white/60 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement des créances...
                </div>
              )}

              {!loadingReceivables &&
                selectedCreditor &&
                receivables.map((row) => (
                  <div
                    key={row.id}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold">
                          {row.reference_number || row.order_number || `#${row.order_id}`}
                        </p>
                        <p className="text-xs text-white/60">
                          {row.order_created_at
                            ? new Date(row.order_created_at).toLocaleString("fr-FR")
                            : "—"}
                        </p>
                        <p className="text-xs text-white/50">
                          Caissier: {row.cashier_name || "—"}
                        </p>
                        <p className="text-xs text-white/50">
                          Montant initial: {formatMoney(row.amount_total || row.total_amount || 0)}
                        </p>
                        {row.note && (
                          <p className="text-xs text-white/50 mt-1">Note: {row.note}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/50">Dû</p>
                        <p className="text-lg font-semibold text-rose-300">
                          {formatMoney(row.amount_due)}
                        </p>
                        <div className="mt-2 flex justify-end gap-2">
                          <button
                            onClick={() => openTicketDetails(row)}
                            className="h-8 w-8 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white flex items-center justify-center"
                            title="Voir le ticket"
                          >
                            <Receipt size={14} />
                          </button>
                          <button
                            onClick={() =>
                              openPayPanel({
                                type: "single",
                                receivable_id: row.id,
                                amount_due: row.amount_due,
                                display_name:
                                  row.reference_number || row.order_number || `#${row.order_id}`,
                              })
                            }
                            className="inline-flex items-center gap-2 rounded-xl border border-emerald-300/40 bg-emerald-400/15 hover:bg-emerald-400/25 px-3 py-1.5 text-xs font-semibold text-emerald-200"
                          >
                            <CreditCard size={14} />
                            Payer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {(error || success) && (
          <div className="px-4 pb-4">
            {error && (
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200 flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            {success && (
              <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200 flex items-center gap-2">
                <CheckCircle2 size={16} />
                {success}
              </div>
            )}
          </div>
        )}
      </div>

      {showPaymentPanel && (
        <div className="fixed inset-0 z-[1010] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-[#111c34] text-white shadow-2xl">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h4 className="text-xl font-semibold">Valider le recouvrement</h4>
              <button
                onClick={closePanel}
                className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm space-y-1">
                <p className="text-white/60">Cible</p>
                <p className="font-semibold">{paymentTarget?.display_name || "Créancier"}</p>
                <p className="text-white/60">
                  Montant dû:{" "}
                  <span className="text-rose-300 font-semibold">
                    {formatMoney(paymentTarget?.amount_due || 0)}
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Montant à payer
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 focus:outline-none focus:border-amber-300"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                    Moyen de paiement
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 focus:outline-none focus:border-amber-300"
                  >
                    <option value="">Sélectionner…</option>
                    {paymentMethods.map((method) => (
                      <option key={method.code || method.id} value={method.code || method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                  Notes
                </label>
                <textarea
                  rows={2}
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="Note du recouvrement (optionnel)"
                  className="w-full rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 focus:outline-none focus:border-amber-300"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
                  Code de validation
                </label>
                <input
                  type="password"
                  value={pinCode}
                  onChange={(e) => setPinCode(e.target.value)}
                  placeholder="Code utilisateur"
                  className="w-full rounded-2xl border border-white/15 bg-white/5 px-3 py-2.5 focus:outline-none focus:border-amber-300"
                />
              </div>

              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm">
                <p className="text-emerald-200 font-semibold mb-1">Récapitulatif</p>
                <p className="text-emerald-100">
                  Vous allez enregistrer un recouvrement de{" "}
                  <span className="font-semibold">{formatMoney(paymentAmount || 0)}</span>{" "}
                  via <span className="font-semibold">{paymentMethod || "—"}</span>.
                </p>
              </div>
            </div>

            <div className="px-5 pb-5 flex justify-end gap-3">
              <button
                onClick={closePanel}
                className="rounded-2xl border border-white/15 bg-white/5 px-4 py-2.5 font-semibold text-white/80 hover:bg-white/10"
              >
                Annuler
              </button>
              <button
                onClick={submitPayment}
                disabled={submitting}
                className="rounded-2xl bg-gradient-to-r from-emerald-400 to-emerald-500 px-4 py-2.5 font-semibold text-slate-900 disabled:opacity-60 inline-flex items-center gap-2"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                Confirmer le recouvrement
              </button>
            </div>
          </div>
        </div>
      )}

      {showTicketModal && (
        <div className="fixed inset-0 z-[1010] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl rounded-3xl border border-white/10 bg-[#111c34] text-white shadow-2xl max-h-[90vh] overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.24em] text-white/50">
                  Ticket
                </p>
                <h4 className="text-xl font-semibold">
                  {ticketTarget?.reference_number ||
                    ticketTarget?.order_number ||
                    (ticketTarget?.order_id ? `#${ticketTarget.order_id}` : "Commande")}
                </h4>
              </div>
              <button
                onClick={closeTicketPanel}
                className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 flex items-center justify-center"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto max-h-[calc(90vh-84px)] space-y-4">
              {loadingTicket && (
                <div className="text-sm text-white/60 flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  Chargement du ticket...
                </div>
              )}

              {!loadingTicket && ticketError && (
                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {ticketError}
                </div>
              )}

              {!loadingTicket && !ticketError && ticketDetails?.order && (
                <>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <p className="text-sm text-white/60">
                      Date: {formatDateTime(ticketDetails?.order?.created_at)}
                    </p>
                    <p className="text-sm text-white/60">
                      Caissier: {ticketDetails?.order?.cashier_names?.join(", ") || "—"}
                    </p>
                    <p className="text-sm text-white/60">
                      Paiement: {ticketDetails?.order?.payment_methods_display || "—"}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <p className="text-sm font-semibold mb-3">Articles</p>
                    <div className="space-y-2">
                      {(ticketDetails?.items || []).map((item) => (
                        <div key={item.id} className="flex items-start justify-between gap-3 text-sm">
                          <div>
                            <p className="font-medium">{formatLineItemName(item)}</p>
                            <p className="text-white/60">x{Number(item.quantity) || 1}</p>
                          </div>
                          <p className="font-semibold text-emerald-300">
                            {formatMoney(item.total_price_with_extras || item.total_price || 0)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/60">Montant initial</span>
                      <span className="font-semibold">
                        {formatMoney(ticketDetails?.order?.financials?.gross_subtotal || ticketDetails?.order?.total_amount || 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-white/60">Total payé</span>
                      <span className="font-semibold text-emerald-300">
                        {formatMoney(ticketDetails?.order?.total_paid_amount || 0)}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditRecoveryModal;
